import { createSlice } from '@reduxjs/toolkit'
import { LOCATION_CHANGE } from 'connected-react-router'
import Favori, { FavoriAPI } from 'interfaces/Favori'
import Parcelle from 'entity/Parcelle'
import { scroller } from 'react-scroll'

export enum Status {
  // Generic
  Idle,
  Pending,
  Succeeded,
  Failed,
  // Custom
  SucceededToggleAdd,
  SucceededToggleRemove,
  ShowModalExportFavoris,
  ShowModalSupprimerFavoris,
  ShowModalDeleteFavoris,
}

export interface FavorisState {
  favoris: { [uid: string]: Favori }
  selected: string | null
  status: Status
  error: string | null
  statusExportFile: Status
  errorExportFile: string | null
  exportFileBlob: Blob | null
  doNotShowModalSupprimerFavoris: boolean
  statusDeleteFavoris: Status
  errorDeleteFavoris: string | null
}

const initialState: FavorisState = {
  favoris: {},
  selected: null,
  status: Status.Idle,
  error: null,
  statusExportFile: Status.Idle,
  errorExportFile: null,
  exportFileBlob: null,
  doNotShowModalSupprimerFavoris: false,
  statusDeleteFavoris: Status.Idle,
  errorDeleteFavoris: null,
}

const name = 'favoris'

const favorisSlice = createSlice({
  name,
  initialState,
  reducers: {
    // Redux Toolkit allows us to write "mutating" logic in reducers. It
    // doesn't actually mutate the state because it uses the Immer library,
    // which detects changes to a "draft state" and produces a brand new
    // immutable state based off those changes
    [LOCATION_CHANGE]: (state, action) => {
      const isFirstRendering = action.payload
      if (isFirstRendering)
        return {
          ...initialState,
          doNotShowModalSupprimerFavoris: state.doNotShowModalSupprimerFavoris,
        }
      return state
    },
    createFavorisFromParcellesResults: (state, action) => {
      const parcelles = action.payload

      state.status = Status.Succeeded
      state.favoris = parcelles.reduce(
        (acc: { [uid: string]: Favori }, parcelle: Parcelle) => {
          const { uid, address, centre, geographie, favori } = parcelle
          return {
            ...acc,
            [uid]: {
              uidParcelle: uid,
              urlAnnonce: favori?.urlAnnonce || null,
              commentaire: favori?.commentaire || null,
              createdAt: favori?.createdAt || null,
              adresseParcelle: address,
              parcelleCentre: centre,
              parcelleGeography: geographie,
              isActive: favori !== null,
              isChecked: false,
              showModalUrlAnnonce: false,
              showModalCommentaire: false,
              statusCommentaire: Status.Idle,
              errorCommentaire: null,
              statusUrlAnnonce: Status.Idle,
              errorUrlAnnonce: null,
              statusToggle: Status.Idle,
              errorToggle: null,
            },
          }
        },
        {}
      )
    },
    idle: (state) => ({
      ...initialState,
      doNotShowModalSupprimerFavoris: state.doNotShowModalSupprimerFavoris,
    }),
    loading: (state) => {
      state.status = Status.Pending
    },
    succeeded: (state, action) => {
      const favoris = action.payload

      return {
        ...state,
        ...initialState,
        favoris: favoris.reduce(
          (acc: { [uid: string]: Favori }, parcelle: FavoriAPI) => ({
            ...acc,
            [parcelle.uidParcelle]: {
              ...parcelle,
              isActive: true,
              isChecked: false,
              statusCommentaire: Status.Idle,
              errorCommentaire: null,
              statusUrlAnnonce: Status.Idle,
              errorUrlAnnonce: null,
              statusToggle: Status.Idle,
              errorToggle: null,
            },
          }),
          {}
        ),
        status: Status.Succeeded,
      }
    },
    failed: (state, action) => {
      const error = action.payload

      state.status = Status.Failed
      state.error = error
    },
    idleCommentaire: (state, action) => {
      const uid = action.payload

      state.favoris[uid].statusCommentaire = Status.Idle
    },
    loadingCommentaire: (state, action) => {
      const uid = action.payload

      state.favoris[uid].statusCommentaire = Status.Pending
    },
    succeededCommentaire: (state, action) => {
      const { uid, commentaire } = action.payload

      state.favoris[uid].commentaire = commentaire
      state.favoris[uid].statusCommentaire = Status.Succeeded
    },
    failedCommentaire: (state, action) => {
      const { uid, error } = action.payload

      state.favoris[uid].statusCommentaire = Status.Failed
      state.favoris[uid].errorCommentaire = error
    },
    idleUrlAnnonce: (state, action) => {
      const uid = action.payload

      state.favoris[uid].statusUrlAnnonce = Status.Idle
    },
    loadingUrlAnnonce: (state, action) => {
      const uid = action.payload

      state.favoris[uid].statusUrlAnnonce = Status.Pending
    },
    succeededUrlAnnonce: (state, action) => {
      const { uid, urlAnnonce } = action.payload

      state.favoris[uid].urlAnnonce = urlAnnonce
      state.favoris[uid].statusUrlAnnonce = Status.Succeeded
    },
    failedUrlAnnonce: (state, action) => {
      const { uid, error } = action.payload

      state.favoris[uid].statusUrlAnnonce = Status.Failed
      state.favoris[uid].errorUrlAnnonce = error
    },
    idleToggle: (state, action) => {
      const uid = action.payload

      state.favoris[uid].statusToggle = Status.Idle
    },
    loadingToggle: (state, action) => {
      const uid = action.payload

      state.favoris[uid].statusToggle = Status.Pending
    },
    succeededToggleAdd: (state, action) => {
      const uid = action.payload

      state.favoris[uid].statusToggle = Status.SucceededToggleAdd
      state.favoris[uid].isActive = true
    },
    succeededToggleRemove: (state, action) => {
      const uid = action.payload

      state.favoris[uid].statusToggle = Status.SucceededToggleRemove
      state.favoris[uid].isActive = false
    },
    failedToggle: (state, action) => {
      const { uid, error } = action.payload

      state.favoris[uid].statusToggle = Status.Failed
      state.favoris[uid].errorToggle = error
    },
    toggleCheckedExport: (state, action) => {
      const uid = action.payload

      state.favoris[uid].isChecked = !state.favoris[uid].isChecked
    },
    checkAllFavoris: (state) => {
      state.favoris = Object.entries(state.favoris).reduce<any>(
        (acc: { [uid: string]: Favori }, [uid, favori]) => ({
          ...acc,
          [uid]: {
            ...favori,
            isChecked: true,
          },
        }),
        {}
      )
    },
    uncheckAllFavoris: (state) => {
      state.favoris = Object.entries(state.favoris).reduce<any>(
        (acc: { [uid: string]: Favori }, [uid, favori]) => ({
          ...acc,
          [uid]: {
            ...favori,
            isChecked: false,
          },
        }),
        {}
      )
    },
    idleExportFile: (state) => {
      state.exportFileBlob = null
      state.statusExportFile = Status.Idle
      state.errorExportFile = null
    },
    loadingExportFile: (state) => {
      state.statusExportFile = Status.Pending
    },
    succeededExportFile: (state, action) => {
      const exportFileBlob = action.payload

      state.exportFileBlob = exportFileBlob
      state.statusExportFile = Status.Succeeded
    },
    failedExportFile: (state, action) => {
      const error = action.payload

      state.statusExportFile = Status.Failed
      state.errorExportFile = error
    },
    showModalExportFavoris: (state) => {
      state.statusExportFile = Status.ShowModalExportFavoris
      state.errorExportFile = null
    },
    selectFavori: (state, action) => {
      const uid = action.payload

      state.selected = uid
    },
    scrollFavoriToTop: (_, action) => {
      const uid = action.payload

      scroller.scrollTo(uid, {
        duration: 1000,
        delay: 0,
        smooth: true,
        offset: -20,
        containerId: 'listing-favoris',
      })
    },
    toggleModalUrlAnnonce: (state, action) => {
      const { uid, show } = action.payload

      state.favoris[uid].showModalUrlAnnonce =
        show !== undefined ? show : !state.favoris[uid].showModalUrlAnnonce
    },
    toggleModalCommentaire: (state, action) => {
      const { uid, show } = action.payload

      state.favoris[uid].showModalCommentaire =
        show !== undefined ? show : !state.favoris[uid].showModalCommentaire
    },
    showModalSupprimerFavoris: (state, action) => {
      const uid = action.payload

      state.favoris[uid].statusToggle = Status.ShowModalSupprimerFavoris
    },
    updateDoNotShowModalSupprimerFavoris: (state, action) => {
      const doNotShowModalSupprimerFavoris = action.payload

      state.doNotShowModalSupprimerFavoris = doNotShowModalSupprimerFavoris
    },
    idleDeleteFavoris: (state) => {
      state.statusDeleteFavoris = Status.Idle
      state.errorDeleteFavoris = null
    },
    loadingDeleteFavoris: (state) => {
      state.statusDeleteFavoris = Status.Pending
      state.errorDeleteFavoris = null
    },
    succeededDeleteFavoris: (state) => ({
      ...state,
      ...initialState,
      statusDeleteFavoris: Status.Succeeded,
      errorDeleteFavoris: null,
    }),
    failedDeleteFavoris: (state, action) => {
      const error = action.payload

      state.statusDeleteFavoris = Status.Failed
      state.errorDeleteFavoris = error
    },
    showModalDeleteFavoris: (state) => {
      state.statusDeleteFavoris = Status.ShowModalDeleteFavoris
      state.errorDeleteFavoris = null
    },
  },
})

export const {
  createFavorisFromParcellesResults,
  idle,
  loading,
  succeeded,
  failed,
  idleCommentaire,
  loadingCommentaire,
  succeededCommentaire,
  failedCommentaire,
  idleUrlAnnonce,
  loadingUrlAnnonce,
  succeededUrlAnnonce,
  failedUrlAnnonce,
  idleToggle,
  loadingToggle,
  succeededToggleAdd,
  succeededToggleRemove,
  failedToggle,
  toggleCheckedExport,
  checkAllFavoris,
  uncheckAllFavoris,
  idleExportFile,
  loadingExportFile,
  succeededExportFile,
  failedExportFile,
  showModalExportFavoris,
  selectFavori,
  scrollFavoriToTop,
  toggleModalUrlAnnonce,
  toggleModalCommentaire,
  showModalSupprimerFavoris,
  updateDoNotShowModalSupprimerFavoris,
  idleDeleteFavoris,
  loadingDeleteFavoris,
  succeededDeleteFavoris,
  failedDeleteFavoris,
  showModalDeleteFavoris,
} = favorisSlice.actions

export default favorisSlice
